'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

const APPROVED_ADMIN_EMAILS = ['waleakinjogbin@gmail.com'];

// ── Auth error normalizer ─────────────────────────────────────────────────────
function normalizeError(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Please confirm your email address before signing in. Check your inbox for a confirmation link.';
  }
  if (lower.includes('too many requests')) {
    return 'Too many login attempts. Please wait a few minutes and try again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  return msg || 'An unexpected error occurred. Please try again.';
}

export default function AdminLoginPage() {
  const router = useRouter();
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (signInError) {
      console.error('[AdminLogin] Sign-in error:', signInError);
      setError(normalizeError(signInError.message));
      setLoading(false);
      return;
    }

    const userId = data?.user?.id;
    if (!userId) {
      setError('Authentication failed. Please try again.');
      setLoading(false);
      return;
    }

    console.info(`[AdminLogin] Authenticated userId=${userId}`);

    // Resolve role — try metadata first (no DB round-trip)
    let role = data?.user?.user_metadata?.role || data?.user?.app_metadata?.role || '';
    console.info(`[AdminLogin] Role from metadata: ${role}`);

    // If role not in metadata, try RPC sync
    if (!role) {
      try {
        const { data: syncedRole } = await supabase.rpc('ensure_admin_role_on_signin', {
          signin_user_id: userId,
          signin_email: email.trim().toLowerCase(),
        });
        role = syncedRole || '';
        console.info(`[AdminLogin] Role from RPC: ${role}`);
      } catch (rpcErr) {
        console.warn('[AdminLogin] Role sync RPC error (non-fatal):', rpcErr);
      }
    }

    // Fallback: read from profiles table
    if (!role) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();
      role = profile?.role || '';
      console.info(`[AdminLogin] Role from profiles: ${role}`);
    }

    console.info(`[AdminLogin] Final resolved role: ${role}`);

    if (role === 'admin') {
      console.info('[AdminLogin] Admin role confirmed — redirecting to dashboard');
      // Use replace to avoid back-button loop to login
      router.replace('/apps/admin');
      return;
    }

    await supabase.auth.signOut();

    if (role === 'vendor') {
      setError('This is the Admin portal. Please use the Vendor portal to sign in.');
    } else if (role === 'driver' || role === 'customer') {
      setError('Access denied. This portal is for Zalchos administrators only.');
    } else {
      setError('Access denied. This portal is for Zalchos administrators only. Contact support if you believe this is an error.');
    }
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      setLoading(false);
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const isApproved = APPROVED_ADMIN_EMAILS.includes(normalizedEmail);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: { role: isApproved ? 'admin' : 'pending', full_name: normalizedEmail.split('@')[0] },
      },
    });

    if (signUpError) {
      console.error('[AdminRegister] Sign-up error:', signUpError);
      setError(normalizeError(signUpError.message));
      setLoading(false);
      return;
    }

    const userId = data?.user?.id;
    if (userId && isApproved) {
      await supabase.from('user_roles').upsert(
        { user_id: userId, email: normalizedEmail, role: 'admin' },
        { onConflict: 'user_id' },
      );
      await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('id', userId);
    }

    if (isApproved) {
      setSuccess('Account created! You have been granted admin access. Please check your email to confirm, then sign in.');
    } else {
      setSuccess('Account created. Your email is not on the approved admin list. Contact the platform owner for admin access.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 py-12">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden border border-border">
              <AppImage
                src="/assets/images/IMG_0046-1768448408663.jpeg"
                alt="Zalchos Logo"
                width={56}
                height={56}
                className="object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-primary mb-1">Zalchos Admin</h1>
          <p className="text-muted-foreground text-sm">Platform Administration Portal</p>
        </div>

        <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
          {/* Mode Toggle */}
          <div className="flex rounded-lg bg-muted p-1 mb-7">
            {(['signin', 'register'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); setError(''); setSuccess(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === m
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Icon + Title */}
          <div className="text-center mb-7">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Icon
                name={mode === 'signin' ? 'ShieldCheckIcon' : 'UserPlusIcon'}
                size={24}
                className="text-primary"
              />
            </div>
            <h2 className="text-xl font-bold text-foreground">
              {mode === 'signin' ? 'Admin Sign In' : 'Create Admin Account'}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {mode === 'signin' ? 'Restricted to Zalchos administrators' : 'Register with your approved admin email'}
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-5 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2.5">
              <Icon name="ExclamationTriangleIcon" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-5 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2.5">
              <Icon name="CheckCircleIcon" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}

          {/* Sign In Form */}
          {mode === 'signin' ? (
            <form onSubmit={handleSignIn} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="EnvelopeIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="admin@zalchos.com"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="LockClosedIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} className="text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end">
                <Link href="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Verifying...
                  </>
                ) : 'Sign In to Admin'}
              </button>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="EnvelopeIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="waleakinjogbin@gmail.com"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Use your pre-approved admin email for automatic admin access.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="LockClosedIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Min. 8 characters"
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} className="text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="LockClosedIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Re-enter password"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-60"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Creating Account...
                  </>
                ) : 'Create Admin Account'}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-border text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              Vendor?{' '}
              <Link href="/apps/vendor/login" className="text-primary hover:underline font-medium">
                Sign in to Vendor Portal
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
